import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { iframeCommunication } from '@/utils/iframeCommunication';

export interface AuthState {
  token: string | null;
  account: string | null;
  loading: boolean;
  error: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    account: null,
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    getAuthHeaders: (state) => ({
      'auth-token': state.token,
      'auth-account': state.account,
    }),
  },

  actions: {
    async authenticate() {
      this.loading = true;
      this.error = null;

      try {
        const route = useRoute();
        const token = route.query.token as string;
        const account = route.query.account as string;
        
        // URL query params are the source of truth
        if (!token || !account) {
          sessionStorage.removeItem('auth_token');
          sessionStorage.removeItem('auth_account');
          this.token = null;
          this.account = null;
          this.loading = false;
          return false;
        }

        // Set the token and account from query parameters
        this.token = token;
        this.account = account;
        
        // Store in sessionStorage for session persistence
        sessionStorage.setItem('auth_token', token);
        sessionStorage.setItem('auth_account', account);
        
        return true;
        
      } catch (e: unknown) {
        const error = e as { userMessage?: string; message?: string };
        this.error = error?.userMessage || error?.message || 'Authentication failed';
        this.loading = false;
        return false;
      } finally {
        this.loading = false;
      }
    },

    initializeFromSession() {
      const token = sessionStorage.getItem('auth_token');
      const account = sessionStorage.getItem('auth_account');
      
      // Set token if available
      if (token) {
        this.token = token;
      }
      
      // Set account if available (can be set independently)
      if (account) {
        this.account = account;
      }
    },

    // Method to clear invalid credentials when 401 error occurs
    clearInvalidCredentials() {
      this.token = null;
      this.account = null;
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_account');
      
      // Notify parent that the session is invalid
      iframeCommunication.sendMessage('session-invalid', {
        reason: 'token-expired-or-invalid'
      });
    },

    logout(options?: { silent?: boolean; reason?: string }) {
      this.token = null;
      this.account = null;
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_account');
      if (!options?.silent) {
        iframeCommunication.sendMessage('logout', {
          reason: options?.reason ?? 'user-initiated'
        });
      }
    },

    // Method to check if we have valid session credentials
    hasValidSession() {
      const token = sessionStorage.getItem('auth_token');
      const account = sessionStorage.getItem('auth_account');
      return !!(token && account);
    },

    // Method to force re-initialization from session storage
    reinitializeFromSession() {
      this.initializeFromSession();
    },
  },
});